import { Box, Flex } from '@utilitywarehouse/hearth-react';
import { Formik } from 'formik';
import { FC, MouseEvent, useEffect, useRef, useState } from 'react';

import { BundleBot } from '@components/BundleBot';
import { DialogID, useDialog } from '@components/Dialog';
import { ErrorIndicator } from '@components/ErrorIndicator';
import { FormikPersist } from '@components/FormikPersist';
import { NavBar } from '@components/NavBar';
import { ScrollToError } from '@components/ScrollToError';
import { UpdatingModal } from '@components/UpdatingModal';
import { DISCOUNT_IDS } from '@constants/apiConstants';
import { CURRENCY_CODE } from '@constants/app';
import { FeatureFlags } from '@constants/featureFlags';
import { BROADBAND } from '@constants/formNames';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import { RemoveDialog } from '@features/RemoveDialog';
import { ProductType } from '@generated/gql/graphql';
import { useFeatureFlag } from '@hooks/useFeatureFlag';
import { BroadbandFormValues } from '@src/types/broadband';
import { BundleBasketData, SelectedServices } from '@src/types/bundle';
import { BooleanSelection } from '@src/types/index';

import { BroadbandFAQ } from './BroadbandFAQ';
import { LineActivation } from './LineActivation';
import { PlanSelection } from './PlanSelection';
import { UpgradeWiFi } from './UpgradeWiFi';
import './styles.scss';
import { useBroadbandSubmitter } from './useBroadbandSubmitter';
import { shouldShowDigitalHomePhone } from './utils/shouldShowDigitalHomePhone';
import { validate } from './validation';

interface BroadbandProps {
  basket: BundleBasketData;
  selectedServices: SelectedServices;
}

const orderedFields: string[] = [
  'selectedTariff',
  'wifiUpgrade',
  'includeVoip',
  'landline',
  'callPackage',
  'callPackageId',
];

const initialValues: BroadbandFormValues = {
  wifiUpgrade: false,
  landline: '',
  callPackage: BooleanSelection.No,
  callPackageId: null,
  selectedTariff: '',
  includeVoip: null,
};

const Broadband: FC<BroadbandProps> = ({ basket, selectedServices }) => {
  const formRef = useRef<HTMLFormElement>(null);

  const updatingDialog = useDialog(DialogID.UPDATING_BUNDLE);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const otherSelectedProductCount = Object.entries(selectedServices).reduce(
    (count, [service, selected]) => {
      if (service === 'broadband' || service === 'cashback') return count;
      if (selected) return count + 1;
      return count;
    },
    0
  );

  const { isLoading, handleSubmit, removeBroadband, removeBroadbandError } =
    useBroadbandSubmitter(basket);

  const { value: isOptionalVoipEnabled } = useFeatureFlag(
    FeatureFlags.OPTIONAL_VOIP,
    false
  );

  useEffect(() => {
    updatingDialog.setOpen(isLoading);
  }, [isLoading, updatingDialog]);

  if (removeBroadbandError) {
    return <ErrorIndicator />;
  }

  const next = (event: MouseEvent) => {
    event.preventDefault();
    formRef?.current?.requestSubmit();
  };
  const selectedValues = basket.broadband.yourService;
  const broadbandSelected = !!selectedValues?.selectedTariff;
  const tbybDiscount = basket.broadband.quotes
    .flatMap((quote) => quote.discounts)
    .find(
      (discount) => discount.id === DISCOUNT_IDS.TBYB_BROADBAND_LINE_DISCOUNT
    );

  const getShowDigitalHomePhone = (tariffId: string) =>
    shouldShowDigitalHomePhone(
      basket?.broadband.quotes.find(({ id }) => id === tariffId)
    );

  return (
    <>
      <div className="wrapper">
        {tbybDiscount && otherSelectedProductCount === 0 && (
          <Box className="broadband__BotContainer" marginBottom="300">
            <BundleBot
              text={[
                '<h4>Top tip!</h4>',
                `Add another service to unlock ${tbybDiscount.creditFrequency} months free Broadband!`,
              ]}
            />
          </Box>
        )}
      </div>
      <Box paddingBottom="600">
        <Formik
          initialValues={selectedValues ?? initialValues}
          onSubmit={handleSubmit}
          validate={(values) =>
            validate(values, {
              showDigitalHomePhone:
                isOptionalVoipEnabled &&
                getShowDigitalHomePhone(values.selectedTariff),
            })
          }
        >
          {({ handleSubmit, setFieldValue, values }) => (
            <>
              <FormSync
                committedValues={
                  (selectedValues ?? undefined) as
                    | Record<string, unknown>
                    | undefined
                }
              />
              <FormikPersist
                name={BROADBAND}
                committedValues={selectedValues}
                suspend={isLoading}
              />
              <form onSubmit={handleSubmit} ref={formRef}>
                <Flex direction="column" spacing="2xl">
                  <PlanSelection basket={basket} />
                  <UpgradeWiFi
                    // Hardcoded on purpose - no way to get the cost before hand
                    price={{ value: '5.00', currency: CURRENCY_CODE }}
                    isSelected={values.wifiUpgrade}
                    onSelect={(val) => setFieldValue('wifiUpgrade', val)}
                  />
                  {values.selectedTariff && (
                    <LineActivation
                      showDigitalHomePhone={getShowDigitalHomePhone(
                        values.selectedTariff
                      )}
                      isOptionalVoipEnabled={isOptionalVoipEnabled}
                    />
                  )}
                </Flex>
                <ScrollToError orderedFields={orderedFields} />
              </form>
            </>
          )}
        </Formik>
      </Box>
      <BroadbandFAQ />
      <RemoveDialog
        isOpen={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        onRemove={() => {
          setIsRemoving(true);
          removeBroadband();
        }}
        productType={ProductType.Broadband}
      />
      <NavBar
        nextLabel={broadbandSelected ? 'Save' : 'Add to bundle'}
        nextDisabled={isLoading}
        onNext={next}
        hasRemoveButton={broadbandSelected}
        onRemove={() => setRemoveDialogOpen(true)}
      />
      <UpdatingModal
        heading="Updating your bundle..."
        productType={ProductType.Broadband}
        isRemoving={isRemoving}
      />
    </>
  );
};

export { Broadband };
